// src/data/arbitrationCases.js
//
// THE BOARD CASE AS A FIRST-CLASS RECORD - the platform layer §5.1 of
// data\YCP_ARBITRATION_LAYER_2026-08-27.md. Ruling taken under PD-19 (see the rulings file for the
// number), withdrawable by number.
//
// ---------------------------------------------------------------------------------------------
// WHAT THIS IS, AND WHAT IT DELIBERATELY IS NOT.
//
// The 2026-08-27 analysis found the honest answer: the platform can ARGUE arbitration and cannot
// DOCKET it. NRAB-00004-260004 had nowhere to live; neither did the carrier's own claim number
// 25-45298-YM-CONYM. This module is where a Board case lives. It is a PURE model - no React, no
// Firestore - exactly as claimLadder.js and appealLadder.js are, so the read boundary is proved by
// scripts\test-arbitration-cases.mjs against real objects, not eyeballed in a browser.
//
// The ACTS that move a case through the Board (notice -> submitted -> award -> comply/dissent) already
// live in src\data\appealLadder.js and are NOT duplicated here (§XI - one source). This module holds
// the case IDENTITY, its LINKAGE (to the internal claim, to its Master File series), its per-case
// DIVISION, its clock fields, and WHO MAY READ OR EDIT IT.
//
// ---------------------------------------------------------------------------------------------
// COMMITTEE ISOLATION IS RULINGS 39 AND 41, APPLIED HERE EXACTLY AS TO A CLAIM.
//
// Ruling 39 (data\YCP_RULINGS_2026-08-27.md:126,155): "A GO-342 officer's ceiling is GO-342... Cross-
// committee read. There is none." A General Chairperson reads only his own committee's matters. The
// composite boundary is (committee, localNumber, gcaSuffix) and 39.2 forbids matching on the GCA
// suffix alone. Ruling 41 (data\YCP_RULINGS_2026-08-27_PART3.md:35) extends the same boundary to the
// documents attached to a claim - "claim attachments follow the claim".
//
// A Board case is the off-property continuation of a claim (or a discipline case), so it inherits the
// SAME boundary its claim has. canRead/canEdit below mirror claimLadder.canRead/canEdit's officer
// branches deliberately, from the same list, so the two cannot silently diverge (§XI). If they ever
// disagree, firestore.rules wins and the page is the defect (ruling 16) - the rules half is
// scripts\apply-arb-firestore-rules and is proved against the emulator by test-firestore-rules.mjs.
//
// ---------------------------------------------------------------------------------------------
// DIVISION IS A PER-CASE FACT, READ, NEVER COMPUTED.
//
// "Whichever division a case finds itself" (Bob) = the `division` field on the docket row, set from
// the Board's Notice/acknowledgement. divisionOf() returns exactly that field and returns null when it
// is blank - it does NOT guess Fourth because the four worked cases are Fourth.
//
// AND THE DIVISION IS READ RATHER THAN COMPUTED EVEN THOUGH THE CRAFT RULE IS NOW SOURCED - which is
// the stronger reason. The current Circular No. 1 (2025) is held and readable at
// data\authorities\NRAB_Circular_No_1_2025-01.txt: it assigns divisions by craft, and yard-service
// employees fall to the FIRST Division (:59-61), the Fourth being the residual for water transport and
// anything "not given to the first, second, and third divisions" (:71-74). Yet the Board docketed the
// four Eckert Yardmaster cases as FOURTH. So the division a case ACTUALLY sits in is the Board's
// per-case assignment, which can differ from the craft rule - and computing it from craft would give
// the WRONG division for the very cases this layer was built against. The platform therefore reads the
// Board's assignment and never computes it. (The 2025 Circular landed on master at §418, after this
// layer's base; the earlier "un-OCR'd, cannot source" rationale was withdrawn when the tree caught up.)
//
// ---------------------------------------------------------------------------------------------
// LIVE vs DEAD_FILE - the record_class vocabulary is the ONE declared at
// data\YCP_DEAD_FILE_MARKING_2026-08-27.md §3.1, the same one ARB-1 in scripts\ycp-verify.js reads.
// This module does NOT invent a second convention (§XI). ABSENT IS A FAILURE, never a synonym for
// LIVE. The four Eckert rows are DEAD_FILE worked examples handled off-platform; isLive() returns
// false for them and no reader treats them as live matters.

const F = Object.freeze;

export const BOARD_CASE_RECORD_CLASSES = F(['LIVE', 'DEAD_FILE']);   // §XI: the DEAD_FILE spec's vocabulary
export const BOARD_CASE_TYPES = F(['claim', 'discipline']);          // docket case_type column

// The docket column names, verbatim from the header of data\YCP_ARBITRATION_DOCKET.csv (VF-2: read,
// never assumed). A row parsed by caseFromRow is keyed on these; nothing here renames a field.
export const DOCKET_COLUMNS = F([
  'case_id', 'board', 'division', 'docket_number', 'case_type', 'linked_claim_id',
  'carrier_claim_number', 'claimant', 'carrier', 'subject_code', 'subject',
  'notice_of_intent_date', 'acknowledged_date', 'submission_due', 'submission_filed_date',
  'master_file_case', 'status', 'source_document', 'notes', 'record_class', 'record_class_note',
]);

function str(v) { return typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim()); }
function isDate(s) { return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')); }

// The class of a case, normalized. Returns '' when absent - and '' is never treated as LIVE anywhere
// below. Anything not in the declared vocabulary is returned as given so a caller can name it in a
// malformed message, exactly as ARB-1 does. Tolerant of BOTH shapes on purpose: a raw docket row
// carries `record_class` (the CSV header) and a parsed case carries `recordClass` (caseFromRow's
// output). Reading only one shape was the first bug this suite caught - isLive() was false on every
// parsed case. The camel form wins where both are present.
export function recordClassOf(c) {
  if (!c) return '';
  const v = (c.recordClass != null && c.recordClass !== '') ? c.recordClass : c.record_class;
  return str(v).toUpperCase();
}

// A case is LIVE only when it says so. Absent, DEAD_FILE, or an unknown value are all "not live".
// This is the honest half: the platform never watches, scopes-open, or acts on a row it does not own.
export function isLive(c) {
  return recordClassOf(c) === 'LIVE';
}

// The per-case division, verbatim, or null. NEVER computed. A live case with no division is a defect
// the caller must surface (validateLiveCase catches it); this accessor just refuses to invent one.
export function divisionOf(c) {
  const d = str(c && c.division);
  return d || null;
}

// Parse one row (an object keyed by DOCKET_COLUMNS, e.g. from a CSV reader) into a normalized case.
// It does not validate liveness - a DEAD_FILE row parses fine and stays DEAD_FILE. It only trims and
// keys the fields this module and its readers use, and preserves the record_class exactly.
export function caseFromRow(row) {
  const r = row || {};
  return {
    caseId: str(r.case_id),
    board: str(r.board),
    division: str(r.division),
    docketNumber: str(r.docket_number),
    caseType: str(r.case_type),
    linkedClaimId: str(r.linked_claim_id) || null,
    carrierClaimNumber: str(r.carrier_claim_number) || null,
    claimant: str(r.claimant),
    carrier: str(r.carrier),
    subjectCode: str(r.subject_code) || null,
    subject: str(r.subject),
    noticeOfIntentDate: str(r.notice_of_intent_date) || null,
    acknowledgedDate: str(r.acknowledged_date) || null,
    submissionDue: str(r.submission_due) || null,
    submissionFiledDate: str(r.submission_filed_date) || null,
    masterFileCase: str(r.master_file_case) || null,
    status: str(r.status),
    sourceDocument: str(r.source_document) || null,
    notes: str(r.notes) || null,
    recordClass: recordClassOf(r),
    recordClassNote: str(r.record_class_note) || null,
    // Isolation fields. The DEAD_FILE worked rows do not carry these (they are off-platform); a LIVE
    // case created in-app MUST, and validateLiveCase enforces it. Kept out of DOCKET_COLUMNS because
    // they are not on the CSV header - they belong to the Firestore record, not the seed file.
    committee: str(r.committee) || null,
    scopeKey: str(r.scopeKey) || str(r.scope_key) || null,
  };
}

// A LIVE case must be scopeable and watchable, or it is a defect - never a silently half-tracked
// matter. Returns { ok, errors:[] }. A DEAD_FILE case is NOT validated here (it is not live and
// nothing acts on it); it is only checked to be well-formed enough to display.
export function validateLiveCase(c) {
  const errors = [];
  if (!isLive(c)) {
    // Not a live case; the only thing that would be a defect is an absent/garbage record_class,
    // which ARB-1 already fails on. Here we simply report it is not live.
    return { ok: true, errors: [], live: false };
  }
  if (!str(c.committee)) {
    errors.push('A live Board case must carry a committee - it inherits the claim’s committee '
      + 'boundary. Without it the case cannot be scoped and is read-closed to everyone but an admin. [R-39] [R-41]');
  }
  if (!str(c.board)) errors.push('A live Board case names the Board (e.g. NRAB).');
  if (!str(c.docketNumber)) errors.push('A live Board case carries the Board’s docket number.');
  if (!divisionOf(c)) errors.push('A live Board case carries the Division as the Board set it (per-case fact, never computed).');
  if (!isDate(str(c.submissionDue))) {
    errors.push('A live Board case carries submission_due as an ISO yyyy-mm-dd date - it is the clock ARB-1 watches.');
  }
  return { ok: errors.length === 0, errors, live: true };
}

// ---------------------------------------------------------------------------------------------
// WHO MAY READ A BOARD CASE. Rulings 39/41 mirrored from claimLadder.canRead.
//
// - Admin reads, and the read is audited (ruling 66 pattern; the audit write is the caller's
//   obligation, made visible by opts.adminReadIsAudited, exactly as claimLadder does it).
// - A General Chairperson reads only his OWN committee's cases (ruling 39: no cross-committee read).
// - A Local Chairperson reads a case whose linked claim's scopeKey is in his GCA scope - visibility
//   that HIS claim progressed off the property - and nothing else. He does not edit it (below).
// - A case with no committee is read-closed to everyone but admin (fail-closed): an unscopeable live
//   record must not leak across committees while it is being fixed.
export function canRead(c, user, opts) {
  if (!c || !user) return false;
  if (user.isAdmin) return true;                                 // ruling 66: read, and it is audited
  const cmte = str(c.committee);
  if (!cmte) return false;                                       // fail-closed: unscopeable => admin only
  if (user.role === 'general_chair') {
    return !!user.committee && user.committee === cmte;          // ruling 39: own committee only
  }
  if (user.role === 'local_chair') {
    // Ruling 41: attachments follow the claim; the case follows the claim's GCA scope. The LC sees
    // that his claim reached the Board, read-only. Scope match is on the composite scopeKey the
    // platform already stores (e.g. 'YM|CSXNL|COLLINWOOD'), never on a bare suffix (ruling 39.2).
    return !!c.scopeKey && Array.isArray(user.scopes) && user.scopes.indexOf(c.scopeKey) !== -1;
  }
  return false;
}

// WHO MAY EDIT A BOARD CASE.
//
// Arbitration is off the property - the General Chairperson's territory (the claim left the LC's hands
// at gc_review, ruling 27c: transfer is freeze). So only the GC of the case's committee edits it, plus
// the admin. A Local Chairperson keeps read (above), never edit - the same shape claimLadder gives an
// LC on a claim that has passed gc_review.
export function canEdit(c, user) {
  if (!c || !user) return false;
  if (user.isAdmin) return true;
  const cmte = str(c.committee);
  if (!cmte) return false;                                       // fail-closed
  if (user.role === 'general_chair') {
    return !!user.committee && user.committee === cmte;          // ruling 39: own committee only
  }
  return false;                                                  // an LC does not edit an off-property matter
}

// The set a user may see, isolation applied. A convenience for a desk/list page so it cannot render a
// case the rules would refuse. DEAD_FILE cases are included only for an admin (they are segregated
// worked examples, ruling 30: segregate, do not destroy) - an officer's list shows LIVE matters.
export function visibleTo(cases, user, opts) {
  const list = Array.isArray(cases) ? cases : [];
  return list.filter(c => {
    if (!isLive(c) && !(user && user.isAdmin)) return false;
    return canRead(c, user, opts);
  });
}

// ---------------------------------------------------------------------------------------------
// THE SUBMISSION CLOCK - the SAME field ARB-1 reads, for the member- and GC-facing UI (§5.3).
//
// §5.3: "The submission clock in the member-facing and GC-facing UI, from the same field ARB-1 reads.
// One source, two readers - not two clocks (§XI)." The SOURCE is submissionDue on the case (the docket
// `submission_due` column). ARB-1 in scripts\ycp-verify.js is one reader (it fails a cycle on a clock
// running out); this is the other (it draws the clock for an officer). Both read the one field, and the
// WARN window matches ARB-1's exactly (21 days) so the page and the verifier never disagree.
//
// DEAD_FILE is NEVER watched, exactly as ARB-1 skips it - and this is the honest reason: on the four
// Eckert rows submission_filed_date is DELIBERATELY BLANK (the platform does not hold the filing dates
// of a GC's off-platform cases), and "a blank here must never be read as 'not filed'"
// (data\YCP_ARBITRATION_DOCKET.csv record_class_note). So a non-live case returns 'not_watched', never
// 'overdue'. Division is drawn per-case with divisionOf() - read, never computed.
export const SUBMISSION_WARN_DAYS = 21;   // matches WARN_DAYS in scripts\ycp-verify.js ARB-1

function daysBetween(fromISO, toISO) {
  return Math.round((Date.parse(toISO + 'T00:00:00Z') - Date.parse(fromISO + 'T00:00:00Z')) / 86400000);
}

// The clock state for the UI. Reads submissionDue / submissionFiledDate off the case (the field ARB-1
// reads) and returns a state a panel can render, plus the per-case division. `today` is passed in
// (yyyy-mm-dd) so the function is pure and testable; it defaults to the real date.
//   states: 'filed' | 'overdue' | 'due_soon' | 'open' | 'no_due_date' | 'not_watched'
export function submissionClock(c, today) {
  const now = isDate(String(today)) ? String(today) : new Date().toISOString().slice(0, 10);
  const division = divisionOf(c);
  if (!isLive(c)) {
    return { state: 'not_watched', due: null, filed: null, daysRemaining: null, division,
      reason: 'This case is not LIVE (' + (recordClassOf(c) || 'no record_class')
        + '); its submission clock is not watched, and a blank filing date here is never read as "not filed".' };
  }
  const due = str(c.submissionDue);
  const filed = str(c.submissionFiledDate);
  if (filed && isDate(filed)) {
    return { state: 'filed', due: due || null, filed, daysRemaining: null, division, reason: null };
  }
  if (!due || !isDate(due)) {
    return { state: 'no_due_date', due: null, filed: null, daysRemaining: null, division,
      reason: 'No submission_due is recorded on this live case - the clock cannot be drawn until it is set.' };
  }
  const d = daysBetween(now, due);
  if (d < 0) return { state: 'overdue', due, filed: null, daysRemaining: d, division,
    reason: 'The submission was due ' + due + ' (' + Math.abs(d) + ' day(s) ago) and no filing date is recorded.' };
  if (d <= SUBMISSION_WARN_DAYS) return { state: 'due_soon', due, filed: null, daysRemaining: d, division,
    reason: 'The submission is due ' + due + ' (' + d + ' day(s) away). No extension is ever granted by the Board.' };
  return { state: 'open', due, filed: null, daysRemaining: d, division, reason: null };
}

export default {
  BOARD_CASE_RECORD_CLASSES, BOARD_CASE_TYPES, DOCKET_COLUMNS, SUBMISSION_WARN_DAYS,
  recordClassOf, isLive, divisionOf, caseFromRow, validateLiveCase,
  canRead, canEdit, visibleTo, submissionClock,
};
